import { RouteAuthorityKey } from '@/router/staticRoute';
import { Local } from '@/utils/storage';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { TenantInfo, TenantResDto, UserInfoType } from '../types';

/**
 * 认证上下文类型定义，用于描述认证相关的状态和方法
 */
interface AuthContextType {
  // menus: MenuItem[]; // 菜单项数组，当前被注释掉
  isAuthenticated: boolean; // 用户是否已认证的布尔值
  login: () => void; // 登录方法，无参数
  logout: () => void; // 登出方法，无参数
  userInfo: UserInfoType; // 用户信息对象
  tenantInfo: TenantInfo; // 租户信息对象
  access_token: string; // 访问令牌字符串
  authButtons: RouteAuthorityKey[]; // 权限按钮数组，包含路由权限键
  useBtnAuth: (key: RouteAuthorityKey | string) => boolean; // 按钮权限验证方法，接收权限键并返回布尔值
  setAuthButtons: React.Dispatch<React.SetStateAction<RouteAuthorityKey[]>>; // 设置权限按钮数组的方法
  routerList: TenantResDto[]; // 路由列表，包含租户响应数据对象
  setRouteData: (routerList: TenantResDto[]) => void; // 设置路由数据的方法
  oneLevelRoutes: TenantResDto[]; // 一级路由数组，包含租户响应数据对象
  setOneLevelRoutes: (routerList: TenantResDto[]) => void; // 设置一级路由数据的方法
  getScoketUrl: () => string; // 获取Socket URL的方法，返回字符串
  setIsAuthenticated: (isAuthenticated: boolean) => void; // 设置认证状态的方法，接收布尔值参数
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // 用户信息
  const [userInfo, setUserInfo] = useState<UserInfoType>({});
  // 租户信息
  const [tenantInfo, setTenantInfo] = useState<TenantInfo>({});
  // 按钮权限
  const [authButtons, setAuthButtons] = useState<RouteAuthorityKey[]>([]);
  // 路由数据
  const [routerList, setRouteList] = useState<TenantResDto[]>([]);
  const access_token = Local.get('ny_pc_token') || '';
  // 一维路由与按钮数组
  const [oneLevelRoutes, setOneLevelRoutes] = useState<TenantResDto[]>([]);
  // 初始化认证状态
  useEffect(() => {
    const token = Local.get('ny_pc_token');
    // setUserInfo(Local.get('userInfo'));
    // setTenantInfo(Local.get('tenantInfo'));
    if (token) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
      // setRouteList([]);
      // setAuthButtons([]);
    }
  }, []);

  useEffect(() => {
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function (key, value) {
      const event = new Event('localStorageChanged');
      // @ts-ignore
      event.key = key;
      // @ts-ignore
      event.value = value;
      document.dispatchEvent(event);
      originalSetItem.apply(this, [key, value]);
    };

    const handleLocalStorageChange = (e: any) => {
      if (e.key === 'userInfo') {
        setUserInfo(JSON.parse(e.value));
      }
      if (e.key === 'tenantInfo') {
        setTenantInfo(JSON.parse(e.value));
      }
    };

    document.addEventListener('localStorageChanged', handleLocalStorageChange);

    return () => {
      document.removeEventListener('localStorageChanged', handleLocalStorageChange);
      localStorage.setItem = originalSetItem;
    };
  }, []);

  const login = () => {
    setIsAuthenticated(true);
  };

  const logout = () => {
    Local.remove('user_menus');
    Local.remove('access_token');
    Local.remove('user_id');
    Local.remove('user_phone');
    Local.remove('user_headImg');
    Local.remove('userInfo');
    Local.remove('tenantInfo');
    setIsAuthenticated(false);
    Local.remove('isFirst');
    Local.clear();
  };
  const setRouteData = (list: TenantResDto[]) => {
    setRouteList(list);
  };
  const useBtnAuth = (key: RouteAuthorityKey | string) => {
    return authButtons.includes(key as RouteAuthorityKey);
  };
  // 获取socket连接地址
  const getScoketUrl = () => {
    const user_id = Local.get('user_id');
    const ws: string = import.meta.env.VITE_WS_URL;
    return `${ws}/v1/webSocket?userId=${user_id}`;
  };
  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      login,
      logout,
      userInfo,
      tenantInfo,
      access_token,
      authButtons,
      useBtnAuth,
      setAuthButtons,
      routerList,
      oneLevelRoutes,
      setOneLevelRoutes,
      setRouteData,
      getScoketUrl,
      setIsAuthenticated,
    }),
    [isAuthenticated, userInfo, tenantInfo, access_token, authButtons, routerList, oneLevelRoutes],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * 退出登录，清除本地存储信息
 */
export const logout = () => {
  Local.clear();
};
